import React, { useMemo } from 'react'
import { Navigate } from 'react-router-dom'
import { useGetAuthenticatedUser } from '../../../Flux/Selector/index.js'
import { isGlobalAdmin } from '../../../Model/User/IUser.js'
import type { Datum } from '../AdminStatsPage/statsAggregations.js'
import { StatsBarChart } from '../AdminStatsPage/StatsBarChart.js'
import { Card } from '../AdminStatsPage/StatsUi.js'
import { SpinChrome } from './SpinChrome.js'
import { InfoTip } from './SpinUi.js'
import { growthData, tsLabel } from './spinMetrics.logic.js'
import { useSelectedRun } from './useSelectedRun.js'
import { useSpinLineage } from './useSpinData.js'

export const SpinGrowthPage = () => {
  const user = useGetAuthenticatedUser()
  const sel = useSelectedRun()
  const { selected } = sel
  const { lineage, loading: lineageLoading } = useSpinLineage(selected?.algorithmType)
  const growth = useMemo<Datum[]>(() => growthData(lineage), [lineage])

  if (!user) return <></>
  if (!isGlobalAdmin(user)) return <Navigate to="/home" replace />

  return (
    <div className="space-y-8">
      <SpinChrome title="SPIN · Growth" sel={sel} />

      {lineageLoading && <p className="text-gray-500">Loading lineage…</p>}
      {!lineageLoading && selected && lineage.length === 0 && (
        <p className="text-gray-500">No recompute lineage for {selected.algorithmType} yet.</p>
      )}
      {!lineageLoading && selected && lineage.length > 0 && (
        <section className="space-y-4">
          <h2 className="text-xl font-semibold">
            Recompute outcomes — {selected.algorithmType} ({lineage.length} runs)
            <InfoTip tip="Per run: how each run's SPINs relate to the previous run's, classified by how many predecessors each new SPIN descends from." />
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card title="SPIN count per run">
              <StatsBarChart data={growth} rotateLabels />
            </Card>
            <Card title="Outcomes per run">
              <div className="overflow-x-auto">
                <table className="table table-xs">
                  <thead>
                    <tr>
                      <th>Run</th>
                      <th>Kind</th>
                      <th className="text-right">SPINs</th>
                      <th className="text-right">Members</th>
                      <th className="text-right">
                        New
                        <InfoTip tip="SPINs with 0 predecessors — a genuinely new cluster this run." />
                      </th>
                      <th className="text-right">
                        1:1
                        <InfoTip tip="Superseded: exactly 1 predecessor — the cluster carried forward unchanged in shape (its identity/metadata may have moved; that's what Drift measures)." />
                      </th>
                      <th className="text-right">
                        Merged
                        <InfoTip tip="SPINs with ≥2 predecessors — two or more prior clusters fused into one this run." />
                      </th>
                      <th className="text-right">
                        Splits
                        <InfoTip tip="Prior SPINs that fanned out into ≥2 SPINs this run — one old cluster broke apart." />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineage.map((g) => (
                      <tr key={g.runId}>
                        <td className="font-mono">{tsLabel(g.startedAt)}</td>
                        <td>{g.kind}</td>
                        <td className="text-right">{g.spins.toLocaleString()}</td>
                        <td className="text-right">{g.members.toLocaleString()}</td>
                        <td className="text-right">{g.newSpins.toLocaleString()}</td>
                        <td className="text-right">{g.superseded.toLocaleString()}</td>
                        <td className="text-right">{g.merged.toLocaleString()}</td>
                        <td className="text-right">{g.splitParents.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        </section>
      )}
    </div>
  )
}
